import { z } from 'zod'
import type { CatalogMetric } from '#/domain/catalog/CatalogMetric'
import type { CatalogPage } from '#/domain/catalog/CatalogRepository'
import type {
  CatalogMetricDto as CatalogMetricDtoSpec,
  CatalogMetricRiskTypeAssignmentDto as CatalogMetricRiskTypeAssignmentDtoSpec,
  PagedResultDtoOfCatalogMetricDto as PagedResultDtoOfCatalogMetricDtoSpec,
} from '#/infrastructure/api/generated'
import type { Assert, SpecContract } from '#/infrastructure/api/SpecContract'

const CatalogMetricRiskTypeAssignmentDtoSchema = z.object({
  RiskType: z.string().nullable().optional().default(null),
  ApplicationId: z.string(),
})

export const CatalogMetricDtoSchema = z
  .object({
    Id: z.string(),
    Name: z.string(),
    Displayname: z.string().optional(),
    Description: z.string().optional(),
    Icon: z.string().optional(),
    Uom: z.string().optional(),
    Datasource: z.string(),
    Active: z.boolean().optional(),
    CategoryName: z.string().optional(),
    CategoryId: z.string().nullable().optional().default(null),
    ApplicationId: z.string().nullable().optional().default(null),
    Applications: z.array(z.string()),
    MinLevel: z.number().nullable().optional().default(null),
    MaxLevel: z.number().nullable().optional().default(null),
    RiskTypes: z.array(CatalogMetricRiskTypeAssignmentDtoSchema).optional().default([]),
  })
  .transform((raw): CatalogMetric => ({
    Id: raw.Id,
    Name: raw.Name,
    Displayname: raw.Displayname,
    Description: raw.Description,
    Icon: raw.Icon,
    Uom: raw.Uom,
    Datasource: raw.Datasource,
    Active: raw.Active,
    CategoryName: raw.CategoryName,
    CategoryId: raw.CategoryId,
    ApplicationId: raw.ApplicationId ?? undefined,
    Applications: raw.Applications,
    MinLevel: raw.MinLevel,
    MaxLevel: raw.MaxLevel,
    RowKey: raw.ApplicationId ? `${raw.Id}:${raw.ApplicationId}` : raw.Id,
    RiskTypes: raw.RiskTypes.map((rt) => ({
      riskType: rt.RiskType,
      applicationId: rt.ApplicationId,
    })),
  }))

export type CatalogMetricResponseDto = z.input<typeof CatalogMetricDtoSchema>
export type CatalogMetricDtoSpecContract = Assert<
  SpecContract<CatalogMetricResponseDto, CatalogMetricDtoSpec>
>

export function toDomain(raw: CatalogMetricResponseDto): CatalogMetric {
  return CatalogMetricDtoSchema.parse(raw)
}

export function toDomainList(raw: CatalogMetricResponseDto[]): CatalogMetric[] {
  return raw.map(toDomain)
}

const CatalogMetricPageDtoSchema = z
  .object({
    Items: z.array(CatalogMetricDtoSchema),
    Total: z.number(),
  })
  .transform((raw): CatalogPage<CatalogMetric> => ({
    items: raw.Items,
    total: raw.Total,
  }))

export type CatalogMetricPageResponseDto = z.input<typeof CatalogMetricPageDtoSchema>
export type CatalogMetricPageDtoSpecContract = Assert<
  SpecContract<CatalogMetricPageResponseDto, PagedResultDtoOfCatalogMetricDtoSpec>
>
export type CatalogMetricRiskTypeAssignmentDtoSpecContract = Assert<
  SpecContract<
    NonNullable<CatalogMetricResponseDto['RiskTypes']>[number],
    CatalogMetricRiskTypeAssignmentDtoSpec
  >
>

export function toDomainPage(raw: CatalogMetricPageResponseDto): CatalogPage<CatalogMetric> {
  return CatalogMetricPageDtoSchema.parse(raw)
}
